/* eslint-disable react/prop-types */
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

/**
 * PageHeader Component
 *
 * A universal, compact, enterprise-level header for all application pages.
 * Features gradient background, title with icon, stats, and optional action buttons.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon to display next to title (can be emoji string, Material-UI icon, or React component)
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle/description text
 * @param {Array<Object>} props.stats - Array of stat objects to display. Each object should have { label, value, icon (optional) }
 * @param {Array<Object>} props.actions - Array of action button objects. Each object should have { label, icon, onClick, ariaLabel, variant (optional), color (optional), show (optional) }
 * @param {Function} props.t - Translation function
 * @param {string} props.translationPrefix - Prefix for translation keys (default: "page")
 * @param {boolean} props.compact - Use compact layout (default: false)
 */
function PageHeader({
  icon,
  title,
  subtitle,
  stats = [],
  actions = [],
  t,
  translationPrefix = "page",
  compact = false,
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));

  // Filter actions based on 'show' property (defaults to true if not specified)
  const visibleActions = actions.filter((action) =>
    action.show !== undefined ? action.show : true
  );

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        py: compact ? { xs: 2, sm: 2.5 } : { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: compact ? "150px" : "200px",
          height: compact ? "150px" : "200px",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.contrastText,
            0.08
          )} 0%, transparent 70%)`,
          borderRadius: "50%",
          transform: "translate(50%, -50%)",
        }}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={{ xs: 2, sm: 0 }}
        sx={{ position: "relative", zIndex: 1 }}
      >
        {/* Left Section: Title, Subtitle, Stats */}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}
          >
            {icon && (
              <Box
                sx={{
                  fontSize: compact ? { xs: 20, sm: 24 } : { xs: 24, sm: 32 },
                }}
              >
                {icon}
              </Box>
            )}
            <Typography
              variant={compact ? "h5" : "h4"}
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.01em",
                fontSize: compact
                  ? { xs: "1.25rem", sm: "1.5rem" }
                  : { xs: "1.25rem", sm: "1.75rem", md: "2rem" },
              }}
            >
              {title}
            </Typography>
            {/* Stats Chip - only show first stat as chip in compact mode */}
            {compact && stats.length > 0 && stats[0].label && (
              <Chip
                label={stats[0].label}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
                  color: theme.palette.primary.contrastText,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(
                    theme.palette.primary.contrastText,
                    0.3
                  )}`,
                }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              fontWeight: 400,
              fontSize: compact
                ? { xs: "0.875rem", sm: "0.875rem" }
                : { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {subtitle}
          </Typography>

          {/* Stats Row - only show in non-compact mode */}
          {!compact && stats.length > 0 && (
            <Box
              sx={{
                display: "flex",
                gap: { xs: 2, sm: 4 },
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              {stats.map((stat, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {stat.icon && (
                    <Box sx={{ fontSize: { xs: 16, sm: 20 } }}>{stat.icon}</Box>
                  )}
                  {stat.value !== undefined && (
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.8,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Right Section: Action Buttons */}
        {visibleActions.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            {isXs || isSm ? (
              // Mobile/Tablet: Icon buttons only
              <>
                {visibleActions.map((action, index) => (
                  <IconButton
                    key={index}
                    aria-label={action.ariaLabel}
                    onClick={action.onClick}
                    size="small"
                    sx={{
                      bgcolor: action.color
                        ? alpha(
                            theme.palette[action.color]?.main ||
                              theme.palette.primary.contrastText,
                            0.15
                          )
                        : alpha(theme.palette.primary.contrastText, 0.15),
                      color: action.color
                        ? theme.palette[action.color]?.contrastText || "#fff"
                        : theme.palette.primary.contrastText,
                      border: `1px solid ${
                        action.color
                          ? alpha(
                              theme.palette[action.color]?.main ||
                                theme.palette.primary.contrastText,
                              0.2
                            )
                          : alpha(theme.palette.primary.contrastText, 0.2)
                      }`,
                      "&:hover": {
                        bgcolor: action.color
                          ? alpha(
                              theme.palette[action.color]?.main ||
                                theme.palette.primary.contrastText,
                              0.25
                            )
                          : alpha(theme.palette.primary.contrastText, 0.25),
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                      position: "relative",
                    }}
                  >
                    {action.icon}
                    {action.badge !== undefined && action.badge > 0 && (
                      <Chip
                        label={action.badge}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          height: 18,
                          minWidth: 18,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          bgcolor: theme.palette.error.main,
                          color: theme.palette.error.contrastText,
                          "& .MuiChip-label": {
                            px: 0.5,
                          },
                        }}
                      />
                    )}
                  </IconButton>
                ))}
              </>
            ) : (
              // Desktop: Full buttons with text
              <>
                {visibleActions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || "contained"}
                    size="small"
                    startIcon={action.icon}
                    onClick={action.onClick}
                    aria-label={action.ariaLabel}
                    sx={{
                      bgcolor: action.color
                        ? alpha(
                            theme.palette[action.color]?.main ||
                              theme.palette.primary.contrastText,
                            0.15
                          )
                        : alpha(theme.palette.primary.contrastText, 0.15),
                      color: action.color
                        ? theme.palette[action.color]?.contrastText || "#fff"
                        : theme.palette.primary.contrastText,
                      backdropFilter: "blur(10px)",
                      border: `1px solid ${
                        action.color
                          ? alpha(
                              theme.palette[action.color]?.main ||
                                theme.palette.primary.contrastText,
                              0.2
                            )
                          : alpha(theme.palette.primary.contrastText, 0.2)
                      }`,
                      px: 2,
                      py: 0.75,
                      borderRadius: 1.5,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "&:hover": {
                        bgcolor: action.color
                          ? alpha(
                              theme.palette[action.color]?.main ||
                                theme.palette.primary.contrastText,
                              0.25
                            )
                          : alpha(theme.palette.primary.contrastText, 0.25),
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                      position: "relative",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography
                        variant="inherit"
                        sx={{ fontWeight: "inherit" }}
                      >
                        {action.label}
                      </Typography>
                      {action.badge !== undefined && action.badge > 0 && (
                        <Chip
                          label={action.badge}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            bgcolor: alpha(
                              theme.palette.primary.contrastText || "#fff",
                              0.3
                            ),
                            color: theme.palette.primary.contrastText || "#fff",
                            border: `1px solid ${alpha(
                              theme.palette.primary.contrastText || "#fff",
                              0.4
                            )}`,
                            "& .MuiChip-label": {
                              px: 0.75,
                            },
                          }}
                        />
                      )}
                    </Stack>
                  </Button>
                ))}
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default PageHeader;
